import React, { FC } from 'react'
import styled from 'styled-components'
import { Text } from 'ui'
import { Step3Props, Organization } from './types'
import { SearchIcon, Vicon, BlackPointUpArrowIcon } from '../icons'
import { SearchInput, StyledCheckBox } from '../SharedStyledComponents'

const FormField = styled.div`
  margin-bottom: 16px;
  width: 100%;
`

const InputContainer = styled.div`
  background-color: #FFFFFF;
  border-radius: 100px;
  padding: 16px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--Neutral-Borders-Default, #D1D1D1);
`

const OrganizationsList = styled.div`
  background-color: #FFFFFF;
  border-radius: 16px;
  margin-top: 16px;
  max-height: 400px;
  overflow-y: auto;
  
  /* Hide scrollbar */
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

const OrganizationItem = styled.div`
  display: flex;
  flex-direction: row-reverse;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #D1D1D1;
  cursor: pointer;
  
  &:last-child {
    border-bottom: 0;
  }
  
  &:hover {
    background-color: #F9F9F9;
  }
`

const OrganizationName = styled.div`
    flex: 1;
    text-align: right;
    padding-right: 8px;
`

// Header for one רשימת ישובים. The lists are defined in the admin panel and are the only
// source of publishable communities, so naming them makes the grouping legible.
// It doubles as a select-all row for the communities shown beneath it.
const ListHeader = styled.div`
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    justify-content: space-between;
    padding: 10px 16px;
    text-align: right;
    background-color: #F4F8FB;
    border-bottom: 1px solid #D1D1D1;
    cursor: pointer;

    &:hover {
      background-color: #EAF2F9;
    }
`

const CheckboxContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
`

// Summary Collapse Styles. Pinned absolutely to the bottom of Step3 so its position doesn't
// shift with the length of the communities list — when expanded it grows upward, overlaying the
// list above. The parent must be position:relative for these offsets to anchor correctly.
const SummaryCollapseContainer = styled.div<{ isExpanded: boolean }>`
  display: flex;
  padding: 8px 16px 8px 16px;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid #1EAFFF;
  background: #EDFAFF;
  cursor: pointer;
  position: absolute;
  bottom: 16px;
  left: 0;
  right: 0;
  z-index: 10;
  box-shadow: 0 -4px 16px rgba(0, 0, 0, 0.08);
`

const MoneySummaryContainer = styled.div<{ isExpanded: boolean }>`
  display: flex;
  flex-direction: row-reverse;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  padding-top: 8px;
  ${props => props.isExpanded ? 'border-top: 1px solid var(--Brand-Borders-Max, #1EAFFF);' : ''}
`

const TitleAndUnderTextContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  align-self: stretch;
  flex-direction: column;
  padding: 0px 8px;
  margin-top: 8px;
`

const BlueBar = styled.div`
    width: 48px;
height: 4px;
flex-shrink: 0;
border-radius: 30px;
background: var(--Brand-Surface-Main, #007EFF);
`

/**
 * Checkbox with a third "some but not all" state. `indeterminate` only exists as a DOM
 * property, so React cannot set it through JSX and it has to be written via a ref.
 */
const TriStateCheckBox: FC<{ checked: boolean; indeterminate: boolean }> = ({ checked, indeterminate }) => {
    const ref = React.useRef<HTMLInputElement>(null)

    React.useEffect(() => {
        if (ref.current) ref.current.indeterminate = indeterminate
    }, [indeterminate])

    return (
        <StyledCheckBox
            ref={ref}
            type="checkbox"
            checked={checked}
            readOnly
            style={{ pointerEvents: 'none' }}
        />
    )
}

const Step3: FC<Step3Props> = ({
    searchText, setSearchText,
    selectedOrganizations, toggleOrganization, setOrganizationsSelected,
    // groupedOrganizations, // Taking from context instead
    selectedCount,
    isSummaryExpanded, setIsSummaryExpanded,
    calculateTotalAmount,
    calculateDays,
    pricePerDay,
    formatDateRange,
    startDate,
    endDate,
    // panResponder not needed for web
    allOrganizations,
    distributionLists,
    isLoadingCommunities,
    communitiesError
}) => {

    // Communities come exclusively from the admin panel's רשימות ישובים — never from the
    // user's own memberships. Each list becomes its own group so the source stays visible;
    // a community that belongs to several lists appears under each of them, and because
    // selection is keyed by community id it stays consistent across those appearances.
    const groupedOrganizations = React.useMemo(() => {
        const needle = searchText.trim().toLowerCase();

        return distributionLists
            .map(list => ({
                letter: list.name,
                organizations: list.communities
                    .filter(c => !needle || c.name.toLowerCase().includes(needle))
                    .map<Organization>(c => ({
                        community: {
                            community: String(c.id),
                            name: c.name
                        }
                    }))
            }))
            .filter(group => group.organizations.length > 0);
    }, [distributionLists, searchText]);

    const hasAnyCommunity = distributionLists.some(list => list.communities.length > 0);

    // Flatten logic handling for toggle if needed, or pass full object
    const handleToggle = (org: any) => {
        toggleOrganization(org.community.community, org);
    }

    // Select-all acts on the communities currently shown under the header, so it stays
    // predictable while a search filter is narrowing the group.
    const toggleWholeList = (group: { organizations: Organization[] }) => {
        const ids = group.organizations.map(o => o.community.community);
        const allSelected = ids.every(id => selectedOrganizations.has(id));
        setOrganizationsSelected(ids, !allSelected);
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', paddingBottom: '88px' }}>
        <div>
            <FormField>
                <InputContainer>
                    <SearchIcon />
                    <SearchInput
                        placeholder="חפש ישוב..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ textAlign: 'right' }}
                    />
                </InputContainer>
            </FormField>

            <Text size="sm" weight="normal" style={{ color: '#888' }}>
                נבחרו {selectedCount} ישובים
            </Text>

            <OrganizationsList>
                {isLoadingCommunities && (
                    <OrganizationItem as="div" style={{ justifyContent: 'center', cursor: 'default' }}>
                        <Text size="sm" weight="normal" style={{ color: '#888' }}>טוען ישובים…</Text>
                    </OrganizationItem>
                )}

                {!isLoadingCommunities && communitiesError && (
                    <OrganizationItem as="div" style={{ justifyContent: 'center', cursor: 'default' }}>
                        <Text size="sm" weight="normal" style={{ color: '#C0392B' }}>{communitiesError}</Text>
                    </OrganizationItem>
                )}

                {!isLoadingCommunities && !communitiesError && !hasAnyCommunity && (
                    <OrganizationItem as="div" style={{ justifyContent: 'center', cursor: 'default' }}>
                        <Text size="sm" weight="normal" style={{ color: '#888' }}>
                            אין ישובים זמינים לפרסום
                        </Text>
                    </OrganizationItem>
                )}

                {groupedOrganizations.map((group) => (
                    <React.Fragment key={group.letter}>
                        <ListHeader onClick={() => toggleWholeList(group)}>
                            <Text size="sm" weight="bold" style={{ color: '#007EFF' }}>
                                {group.letter}
                            </Text>
                            <CheckboxContainer>
                                <TriStateCheckBox
                                    checked={group.organizations.every(o => selectedOrganizations.has(o.community.community))}
                                    indeterminate={
                                        group.organizations.some(o => selectedOrganizations.has(o.community.community)) &&
                                        !group.organizations.every(o => selectedOrganizations.has(o.community.community))
                                    }
                                />
                            </CheckboxContainer>
                        </ListHeader>
                        {group.organizations.map((org, index) => (
                            <OrganizationItem
                                key={`${group.letter}-${org.community.community}`}
                                onClick={() => handleToggle(org)}
                            >
                                <OrganizationName>
                                    <Text size="md" weight="bold" style={{ color: '#272727' }}>
                                        {org.community.name}
                                    </Text>
                                </OrganizationName>
                                <CheckboxContainer>
                                    <StyledCheckBox
                                        type="checkbox"
                                        checked={selectedOrganizations.has(org.community.community)}
                                        readOnly
                                        style={{ pointerEvents: 'none' }} // Let container handle click
                                    />
                                </CheckboxContainer>
                            </OrganizationItem>
                        ))}
                    </React.Fragment>
                ))}
            </OrganizationsList>
                    </div>
            {selectedOrganizations.size > 0 && (
                <SummaryCollapseContainer
                    isExpanded={isSummaryExpanded}
                    onClick={() => setIsSummaryExpanded(!isSummaryExpanded)}
                >
                    <BlueBar />

                    {isSummaryExpanded && (
                        <>
                                     {startDate && endDate && (
                                <TitleAndUnderTextContainer>
                                    <Text size="md" weight="bold" style={{ color: '#007EFF' }}>
                                        תוקף הטבה
                                    </Text>
                                    <Text size="sm" weight="normal" style={{ color: '#007EFF' }}>
                                        {formatDateRange()}
                                    </Text>
                                </TitleAndUnderTextContainer>
                            )}
                                     <TitleAndUnderTextContainer>
                                <Text size="md" weight="bold" style={{ color: '#007EFF' }}>
                                    מספר ישובים
                                </Text>
                                <Text size="sm" weight="normal" style={{ color: '#007EFF' }}>
                                    {selectedOrganizations.size}
                                </Text>
                            </TitleAndUnderTextContainer>
                            <TitleAndUnderTextContainer>
                                <Text size="md" weight="bold" style={{ color: '#007EFF' }}>
                                    חישוב התשלום
                                </Text>
                                <Text size="sm" weight="normal" style={{ color: '#007EFF' }}>
                                    ₪{pricePerDay} × {selectedOrganizations.size} ישובים × {calculateDays()} ימים
                                </Text>
                            </TitleAndUnderTextContainer>
                   
               
                        </>
                    )}

                    <MoneySummaryContainer isExpanded={isSummaryExpanded}>
                        <Text size="sm" weight="normal" style={{ color: '#007EFF' }}>
                            {calculateTotalAmount().toFixed(0)}₪
                        </Text>
                        <Text size="md" weight="bold" style={{ color: '#007EFF' }}>
                            סך הכל לתשלום |
                        </Text>
                    </MoneySummaryContainer>
                </SummaryCollapseContainer>
            )}
        </div>
    )
}

export default Step3
